import { PermitFieldType } from '~/client/graph'
import {
  companyPermitFieldTypes,
  locationPermitFieldTypes,
  materialPermitFieldTypes,
  nonHiddeablePermitFieldTypes,
  selectOptionsPermitFieldTypes,
  userPermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import WorkflowType, {
  DefaultWorkflowType,
} from '~/client/src/shared/models/WorkflowType'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'

/** Reads `locationField`, the accessor the seeder writes the pin into, so offer and save agree. */
export function acceptsPreciseLocation(type: WorkflowType): boolean {
  return !!type?.locationField?.allowPreciseLocation
}

/** Derive from this rather than snapshot it — the flag is admin-editable. */
export function getMapCreatableWorkflowTypes(
  permitTypesStore: WorkflowTypesStore,
): WorkflowType[] {
  return permitTypesStore.enabledTypes.filter(acceptsPreciseLocation)
}

// Falls back past Activity so the flow survives its Precise Location being turned off.
export function getMapCreateWorkflowType(
  permitTypesStore: WorkflowTypesStore,
): WorkflowType {
  const creatableTypes = getMapCreatableWorkflowTypes(permitTypesStore)
  const activity = permitTypesStore.getActualTypeByType(
    DefaultWorkflowType.Activity,
  )
  return (
    creatableTypes.find(type => type.id === activity?.id) ??
    creatableTypes[0] ??
    null
  )
}

/** Takes the session id, never a prop: callers run inside the action that just set it. */
export function resolveCreationWorkflowType(
  permitTypesStore: WorkflowTypesStore,
  pickedTypeId: string,
  fallbackType: WorkflowType,
): WorkflowType {
  return pickedTypeId
    ? permitTypesStore.getPermitTypeById(pickedTypeId)
    : fallbackType
}

// Types the composer renders as a row: served by the picker layer AND labelled by a
// mapper, so the row shows a name rather than an id. Deliberately excludes
// linked-workflow/signature/materialResource — pickable, but their compact label
// would be an id, so a mandatory one routes to the full form instead.
export const COMPOSER_ROW_TYPES = new Set<PermitFieldType>([
  ...locationPermitFieldTypes,
  ...companyPermitFieldTypes,
  ...userPermitFieldTypes,
  ...materialPermitFieldTypes,
  ...selectOptionsPermitFieldTypes,
  PermitFieldType.StartFinishDates,
  PermitFieldType.AdditionalDate,
])

// Rows, plus the presentational types: they render nothing and are never mandatory,
// so they must not make a type look incompletable.
const COMPOSER_EDITABLE_TYPES = new Set<PermitFieldType>([
  ...COMPOSER_ROW_TYPES,
  ...nonHiddeablePermitFieldTypes,
  PermitFieldType.Instructions,
])

/** False when the type has a mandatory field only the full form can fill — the
 *  composer would open with Submit permanently disabled. Reads the config, not the
 *  role: isFieldValid short-circuits for admins, so a role-based check would pass
 *  for them and trap everyone else. */
export function isComposerCompletable(type: WorkflowType): boolean {
  const step = type?.initialStep
  if (!step) {
    return true
  }
  // Conditional children count: picking their parent option makes them mandatory and
  // no row can fill one. Depth-1, like WorkflowType.allWorkflowFieldsFlat.
  const fields = [
    ...(step.fields ?? []),
    ...(step.conditionalFields ?? []).flatMap(
      conditional => conditional?.values ?? [],
    ),
  ]
  return !fields.some(
    field =>
      field.isShown &&
      field.isMandatory &&
      !COMPOSER_EDITABLE_TYPES.has(field.type),
  )
}
